import React from 'react'
import { Client } from '@ideal-postcodes/core-browser'

import Field, { IProps as IFieldProps } from '@utilities/Field'
import FieldContainer from './FieldContainer'

import styles from './styles.module.scss'
import { IIdealPostcodesAddress } from './types'
import { IAddress } from '@features/tenants/types'

interface IState {
  searchValue: string
  hasSearched: boolean
  addresses: IAddress[]
  addressVisible: boolean
}

class AddressField extends Field<{}, IState> {
  idealPostcode = new Client({ api_key: 'ak_k2m2r86mRuT4qRjhjnX2wpLriSqLk' })
  searchInput: any = null

  constructor(props: IFieldProps) {
    super(props)
    let { value } = props.field
    let initialAddress = ''

    if (value) {
      initialAddress = this.formatAddress(value)
    }

    this.state = {
      searchValue: initialAddress,
      hasSearched: initialAddress.trim() !== '',
      addresses: [],
      addressVisible: false
    }
  }

  componentDidMount() {
    // Disable autocomplete
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.setAttribute('autocomplete', 'off')
      }
    }, 1000)
  }

  showAddress = (ev: any) => {
    ev.preventDefault()
    this.setState({
      addressVisible: true
    })
  }

  hideAddress = (ev: any) => {
    ev.preventDefault()
    this.setState({
      addressVisible: false
    })
  }

  clearAddressFields = () => {
    let { name, onChange } = this.props
    onChange(name, '')
  }

  handleFocus = (_?: any) => {
    const { name, onFocus } = this.props
    if (onFocus) {
      onFocus(name)
    }
  }

  handleBlur = (_?: any) => {
    const { name, onBlur } = this.props
    // Timeout required to allow selection of address to register before validating
    setTimeout(() => {
      onBlur(name)
    }, 50)
  }

  handleSearchChange = (ev: any) => {
    this.setState({
      searchValue: ev.target.value
    })

    // Search for the address and update the list of addresses to chose from
    this.idealPostcode.lookupAddress({ query: ev.target.value }).then((addresses: any) => {
      this.setState({
        addresses: addresses.map(this.deserializeIdealPostcodeAddress)
      })
    })
  }

  deserializeIdealPostcodeAddress = (address: IIdealPostcodesAddress): IAddress => {
    return {
      abodeno: address.sub_building_number,
      buildingno: address.building_number,
      buildingname: address.building_name,
      street1: address.line_1,
      street2: address.line_2,
      posttown: address.post_town,
      postcode: address.postcode
    }
  }

  handleAddressFieldChange = (ev: any) => {
    let { name, field, onChange } = this.props
    let addressFieldName = ev.target.getAttribute('data-name')
    onChange(name, {
      ...(field.value || {}),
      [addressFieldName]: ev.target.value
    })
  }

  resetSearch = (ev: any) => {
    ev.preventDefault()
    let { name, onChange } = this.props

    // Clear the address
    onChange(name, null)

    // Re-enable the search
    this.setState({
      searchValue: '',
      hasSearched: false,
      addresses: []
    })
  }

  formatAddress = (address: IAddress) => {
    let { abodeno, street1, street2, posttown, postcode } = address
    return `${abodeno ? abodeno.trim() + ', ' : ''}
${street1 ? street1.trim() + ', ' : ''}
${street2 ? street2.trim() + ', ' : ''}
${posttown ? posttown.trim() + ', ' : ''}
${postcode.trim()}`
  }

  selectAddress = (ev: any) => {
    let { name, onChange } = this.props
    let { addresses } = this.state
    let address = addresses[ev.target.getAttribute('data-index')]

    // Prevent editing the search field
    this.setState({
      searchValue: this.formatAddress(address),
      hasSearched: true
    })

    // Update the address field values
    onChange(name, address)

    // Blur to validate fields
    this.handleBlur()
  }

  render() {
    const { name, field, info, icon, label, optional } = this.props
    let { searchValue, addresses, addressVisible, hasSearched } = this.state
    let { value } = field
    let inputClassName = 'Input'

    let searchField: any = (
      <input
        ref={(el) => (this.searchInput = el)}
        disabled={hasSearched}
        autoComplete="none"
        id={name}
        name={name}
        placeholder={this.props.placeholder || 'Start typing your address..'}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onChange={this.handleSearchChange}
        className={inputClassName}
        value={searchValue}
      />
    )
    let toggleButton: any = addressVisible ? (
      <button onClick={this.hideAddress} className="Button -light-grey">
        Hide address
      </button>
    ) : (
      <button onClick={this.showAddress} className="Button -light-grey">
        Enter manually
      </button>
    )

    return (
      <>
        <FieldContainer field={field} info={info} icon={icon} label={label} optional={optional}>
          {searchField}
        </FieldContainer>

        <ul
          className="Menu -mod-margin-top"
          style={{ display: !hasSearched && addresses.length > 0 ? 'block' : 'none' }}
        >
          {addresses.map((address: IAddress, index) => {
            let formattedAddress = this.formatAddress(address)
            return (
              <li key={formattedAddress} data-index={index} onClick={this.selectAddress}>
                {formattedAddress}
              </li>
            )
          })}
        </ul>

        <div className="Row -spaced -mod-margin-bottom -mod-margin-top">
          {toggleButton}
          {hasSearched ? (
            <button onClick={this.resetSearch} className="Button -light-grey">
              Search again
            </button>
          ) : null}
        </div>

        <div
          className={`AddressFields ${styles.postcode_field} ${
            addressVisible ? 'is-active ' : ''
          }-mod-margin-top`}
        >
          <div className="Field -is-required">
            <label className="Label -mod-accessibility-text" htmlFor={`${name}_abodeno`}>
              Apartment no.
            </label>
            <div className="Control">
              <input
                data-name="abodeno"
                className="Input"
                placeholder="Apartment no."
                autoComplete="off"
                id={`${name}_abodeno`}
                name={`${name}_abodeno`}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleAddressFieldChange}
                value={(value && value.abodeno) || ''}
              />
            </div>
          </div>

          <div className="Field -is-required">
            <label className="Label -mod-accessibility-text" htmlFor={`${name}_buildingno`}>
              Building no.
            </label>
            <div className="Control">
              <input
                data-name="buildingno"
                className="Input"
                placeholder="Building no."
                autoComplete="off"
                id={`${name}_buildingno`}
                name={`${name}_buildingno`}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleAddressFieldChange}
                value={(value && value.buildingno) || ''}
              />
            </div>
          </div>

          <div className="Field -is-required">
            <label className="Label -mod-accessibility-text" htmlFor={`${name}_buildingname`}>
              Building name
            </label>
            <div className="Control">
              <input
                data-name="buildingname"
                className="Input"
                placeholder="Building name"
                autoComplete="off"
                id={`${name}_buildingname`}
                name={`${name}_buildingname`}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleAddressFieldChange}
                value={(value && value.buildingname) || ''}
              />
            </div>
          </div>

          <div className="Field -is-required">
            <label className="Label -mod-accessibility-text" htmlFor={`${name}_street1`}>
              Street line one
            </label>
            <div className="Control">
              <input
                data-name="street1"
                className="Input"
                placeholder="Street line one"
                autoComplete="off"
                id={`${name}_street1`}
                name={`${name}_street1`}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleAddressFieldChange}
                value={(value && value.street1) || ''}
              />
            </div>
          </div>

          <div className="Field -is-required">
            <label className="Label -mod-accessibility-text" htmlFor={`${name}_street2`}>
              Street line two
            </label>
            <div className="Control">
              <input
                data-name="street2"
                className="Input"
                placeholder="Street line two"
                autoComplete="off"
                id={`${name}_street2`}
                name={`${name}_street2`}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleAddressFieldChange}
                value={(value && value.street2) || ''}
              />
            </div>
          </div>

          <div className="Field -is-required">
            <label className="Label -mod-accessibility-text" htmlFor={`${name}_posttown`}>
              City / town
            </label>
            <div className="Control">
              <input
                data-name="posttown"
                className="Input"
                placeholder="City / town"
                autoComplete="off"
                id={`${name}_posttown`}
                name={`${name}_posttown`}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleAddressFieldChange}
                value={(value && value.posttown) || ''}
              />
            </div>
          </div>

          <div className="Field -is-required">
            <label className="Label -mod-accessibility-text" htmlFor={`${name}_postcode`}>
              Postcode
            </label>
            <div className="Control">
              <input
                data-name="postcode"
                className="Input"
                placeholder="Postcode"
                autoComplete="off"
                id={`${name}_postcode`}
                name={`${name}_postcode`}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleAddressFieldChange}
                value={(value && value.postcode) || ''}
              />
            </div>
          </div>
        </div>
      </>
    )
  }
}

export default AddressField
